import {
  Component,
  OnInit,
  OnDestroy,
  ViewContainerRef,
  ViewChild,
} from '@angular/core';
import { DndDropEvent } from 'ngx-drag-drop';
import { UUID } from 'angular2-uuid';
import * as _ from 'lodash';
import { NgbModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '@app/form-builder/shared/confirmation-modal/confirmation-modal.component';
import { PreviewHtmlComponent } from '@app/form-builder/shared/preview-html/preview-html.component';
import socialIcons from '@app/form-builder/shared/drag-drop-content/elements/social-property/social-icons';
import { GeneratedCodeModalComponent } from './generated-code-modal/generated-code-modal.component';
import { FormBuilderService } from './form-builder-service.service';
import { I18nService } from '@app/form-builder/shared/i18n.service';
import { ComponentSettingInterface } from '@app/form-builder/shared/drag-drop-content/interfaces/component-setting.interface';
import { FormInterface } from '@app/form-builder/shared/drag-drop-content/interfaces/form.interface';
import { ElementInterface } from '@app/form-builder/shared/drag-drop-content/interfaces/element.interface';
import { BreadCrumb } from '@app/interfaces';
import { FormService, LocalStorageService } from '@app/services';
import { BUTTON, CHECKBOX, DROPDOWN, INITIAL_FORM, INPUT, LOCATION_SELECTOR, OUTPUT, PICTURE, RADIO_BUTTON, SEPERATOR, SOCIAL, SPACER, TABLE, TEXT, TEXTAREA, TOGGLE, UPLOADER } from "./form-builder.config";
import { ActivatedRoute, Router } from '@angular/router';
import Notify from 'devextreme/ui/notify';
import { AppNotification } from '@app/helpers';
import { APP_ROUTES } from '@app/constants';

@Component({
  selector: 'app-form-builder',
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.scss'],
})
export class FormBuilderComponent implements OnInit, OnDestroy {
  @ViewChild('popover') popover!: NgbPopover;
  formEditable: boolean = true;
  formViewClass: string = 'tablet-mode';

  logicComponent = JSON.parse(JSON.stringify(TEXT));

  crumbpath: BreadCrumb[] = [{
    name: 'Home',
    path: '/home',
  }, {
    name: 'Survey',
    path: '/survey',
  }, {
    name: 'Form',
    path: '/form',
  }, {
    name: 'Form Builder',
    path: '/form-builder',
    active: true
  }]
  formId: string | null;
  surveyId: string | null;
  groupId: string | null;
  /**
   * Array for containing dropped components in a form
   */
  addedComponents!: Array<ElementInterface>;

  /**
   * Object for containing drag & drop form
   */
  subscriptionForm: FormInterface = {
    content_dnd: INITIAL_FORM,
  };

  /**
   * Property for containing which tab is activated
   * 1: Current Element
   * 2: Form Design
   */
  public activeTab = 2;

  /**
   * Array for containing only section components
   */
  public sectionComponents: Array<ElementInterface> = [];

  /**
   * Array for containing components except section component
   */
  public components: Array<ElementInterface> = [];

  /**
   * Property for containing class name when dragging element
   */
  public dragStartClass: string = '';

  /**
   * Object for containing current selected component
   */
  public selectedComponent!: null | ElementInterface;

  /**
   * Object for containing component setting properties
   */
  protected componentSettings: ComponentSettingInterface = {
    background_color: '',
    rounded_corners: 0,
    padding_top: 10,
    padding_right: 0,
    padding_bottom: 10,
    padding_left: 0,
    height: 0,
  };

  /**
   * Current language ISO code
   */
  public currentLanguage: string = '';

  constructor(
    public vcRef: ViewContainerRef,
    private modalService: NgbModal,
    private translate: TranslateService,
    private formBuilderService: FormBuilderService,
    private i18nService: I18nService,
    private formService: FormService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  /**
   * On Init handler
   */
  ngOnInit() {
    this.formId = this.route.snapshot.queryParamMap.get('formId');
    this.surveyId = this.route.snapshot.queryParamMap.get('surveyId');
    this.groupId = this.route.snapshot.queryParamMap.get('groupId');
    document.body.classList.add('drag-drop-page');

    this.currentLanguage = this.i18nService.language;

    this.sectionComponents = [
      {
        name: this.translate.instant('1 columns'),
        machineName: 'section',
        image: 'assets/col1.png',
        column: '1',
        addedComponents: new Array(1).fill([] as ElementInterface[]),
        deletable: true,
        element: {
          conditional: false,
          ref_field_name: null,
          ref_field_value: null
        }
      },
      {
        name: this.translate.instant('2 columns'),
        machineName: 'section',
        image: 'assets/col2.png',
        column: '2',
        addedComponents: new Array(2).fill([] as ElementInterface[]),
        deletable: true,
        element: {
          conditional: false,
          ref_field_name: null,
          ref_field_value: null
        }
      },
      {
        name: this.translate.instant('3 columns'),
        machineName: 'section',
        image: 'assets/col3.png',
        column: '3',
        addedComponents: new Array(3).fill([] as ElementInterface[]),
        deletable: true,
        element: {
          conditional: false,
          ref_field_name: null,
          ref_field_value: null
        }
      },
      {
        name: this.translate.instant('4 columns'),
        machineName: 'section',
        image: 'assets/col4.png',
        column: '4',
        addedComponents: new Array(4).fill([] as ElementInterface[]),
        deletable: true,
        element: {
          conditional: false,
          ref_field_name: null,
          ref_field_value: null
        }
      },
      // {
      //   name: this.translate.instant('5 columns'),
      //   machineName: 'section',
      //   image: 'assets/col5.png',
      //   column: '5',
      //   addedComponents: new Array(5).fill([] as ElementInterface[]),
      //   deletable: true,
      // },
      {
        name: this.translate.instant('2 Columns (2:1)'),
        machineName: 'section',
        image: 'assets/col2-1-3.png',
        column: '2-1-3',
        addedComponents: new Array(2).fill(<ElementInterface[]>[]),
        deletable: true,
        element: {
          conditional: false,
          ref_field_name: null,
          ref_field_value: null
        }
      },
      {
        name: this.translate.instant('2 Column (1:2)'),
        machineName: 'section',
        image: 'assets/col2-3-1.png',
        column: '2-3-1',
        addedComponents: new Array(2).fill([] as ElementInterface[]),
        deletable: true,
        element: {
          conditional: false,
          ref_field_name: null,
          ref_field_value: null
        }
      },
    ];

    this.components = [
      LOCATION_SELECTOR,
      TABLE,
      BUTTON,
      INPUT,
      OUTPUT,
      TEXTAREA,
      DROPDOWN,
      RADIO_BUTTON,
      CHECKBOX,
      TOGGLE,
      TEXT,
      PICTURE,
      SPACER,
      SEPERATOR,
      SOCIAL,
      UPLOADER
    ];
    this.getFormInfo()
  }

  /**
   * On destroy handler
   */
  ngOnDestroy() {
    document.body.classList.remove('drag-drop-page');
  }

  /**
   * On preview handler
   */
  onPreview() {
    const modalRef = this.modalService.open(PreviewHtmlComponent, {
      size: 'lg',
      windowClass: 'full-width',
      centered: true,
      scrollable: false,
    });
    this.formBuilderService.components =
      this.subscriptionForm.content_dnd.added_components;
    this.formBuilderService.formDesign =
      this.subscriptionForm.content_dnd.form_design;

    modalRef.componentInstance.content = this.formBuilderService.generateHtml();
  }

  /**
   * On drag start event handler
   *
   * @param event
   */
  onDragStart(event: DragEvent) {
    this.dragStartClass = 'bg-light';
  }

  /**
   * On dragged event handler
   *
   * @param item
   * @param list
   * @param effect
   */
  onDragged(
    item: ElementInterface,
    list: Array<ElementInterface>,
    effect: string
  ) {
    if (effect === 'move') {
      const index = list.findIndex((i: any) => {
        if (item.id && item.id === i.id) {
          return true;
        }

        return false;
      });

      list.splice(index, 1);
    }
  }

  /**
   * On drag end event handler
   *
   * @param event
   */
  onDragEnd(event: DragEvent) {
    this.dragStartClass = '';
  }

  /**
   * On dropped event hanlder
   *
   * @param event
   * @param list
   * @returns
   */
  onDrop(event: DndDropEvent, list: Array<ElementInterface>) {
    this.closePopover();

    if (['copy', 'move'].indexOf(event.dropEffect) === -1) {
      return false;
    }

    let index = event.index;

    if (typeof index === 'undefined') {
      index = list.length;
    }

    // Reset selected state
    this.resetAllSelectedState();

    const component = _.clone(event.data);
    component.isSelected = true;
    component.id = UUID.UUID();

    if (
      [
        'radios',
        'dropdown',
        'input',
        'checkbox',
        'textarea',
        'uploader',
        'toggle',
        'table',
        'output'
      ].includes(component.machineName)
    ) {
      component.element.field_name = `${component.machineName}-${component.id
        .split('-')
        .shift()}`;
    }

    if (
      component.machineName === 'radios' ||
      component.machineName === 'dropdown'
    ) {
      for (let option of component.element.options) {
        option.id = UUID.UUID();
      }
    }

    if (!component.hasOwnProperty('componentSettings')) {
      component.componentSettings = _.clone(this.componentSettings);
    }

    this.selectedComponent = component;
    list.splice(index, 0, component);

    this.activeTab = 1;

    return true;
  }

  /**
   * Set component as selected
   *
   * @param $event
   * @param component
   */
  setSelectedComponent($event: any, component: ElementInterface) {
    $event.stopPropagation();
    this.resetAllSelectedState();
    component.isSelected = true;
    this.selectedComponent = component;

    this.activeTab = 1;
  }

  /**
   * Reset all selected elements's state
   */
  resetAllSelectedState() {
    this.subscriptionForm.content_dnd.added_components.forEach((c: any) => {
      c.isSelected = false;

      if (c.addedComponents.length > 0) {
        c.addedComponents.forEach((cc: Array<any>) => {
          cc.forEach((ccc) => {
            ccc.isSelected = false;
          });
        });
      }
    });
  }

  /**
   * Delete selected component
   *
   * @param $event
   * @param id
   */
  deleteComponent($event: any, id: string) {
    $event.stopPropagation();

    const modalConfirm = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });

    modalConfirm.componentInstance.title = this.translate.instant(
      'Delete confirmation'
    );
    modalConfirm.componentInstance.message = this.translate.instant(
      'Are you sure, that you want to delete this element?'
    );

    modalConfirm.result.then(
      (result) => {
        if (result === 'OK_CLICK') {
          let index = -1;
          for (let c of this.subscriptionForm.content_dnd.added_components) {
            index++;
            if (c.id === id) {
              this.subscriptionForm.content_dnd.added_components.splice(
                index,
                1
              );
              break;
            }

            if (c.addedComponents.length) {
              for (let cc of c.addedComponents) {
                let cIndex = _.findIndex(cc, { id: id });

                if (cIndex > -1) {
                  cc.splice(cIndex, 1);
                  break;
                }
              }
            }
          }

          this.selectedComponent = null;
        }
      },
      (reason) => { }
    );
  }

  /**
   * Close Add section popup
   */
  closePopover() {
    if (this.popover.isOpen()) this.popover.close();
  }

  /**
   * Generate form builder to HTML code
   *
   * @return string
   */
  generateHtml() {
    const modalRef = this.modalService.open(GeneratedCodeModalComponent, {
      size: 'lg',
    });

    this.formBuilderService.components =
      this.subscriptionForm.content_dnd.added_components;
    this.formBuilderService.formDesign =
      this.subscriptionForm.content_dnd.form_design;

    modalRef.componentInstance.title = this.translate.instant('HTML code');
    modalRef.componentInstance.content = this.formBuilderService.generateHtml();
  }

  /**
   * Generate form builder to JSON code
   *
   * @return string
   */
  generateJson() {
    const modalRef = this.modalService.open(GeneratedCodeModalComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.title = this.translate.instant('JSON code');
    modalRef.componentInstance.content = JSON.stringify(
      this.subscriptionForm.content_dnd,
      null,
      2
    );
  }

  /**
   * Change application language
   *
   * @param langCode
   */
  changeLanguage(langCode: string) {
    this.currentLanguage = langCode;
    this.i18nService.language = langCode;
  }

  formName: string = "Form";
  onSave() {
    if (this.surveyId == null) {
      return AppNotification.error("Survey Missing!")
    }
    if (this.formId != null) {
      return this.formService.update(this.formId, {
        "name": this.formName,
        "survey": this.surveyId,
        "formState": {},
        "formJson": JSON.stringify(this.subscriptionForm.content_dnd)
      }).subscribe({
        next: (res: any) => {
          if (res.data?._id) {
            this.formId = res.data?._id
            this.router.navigate([APP_ROUTES.FORM_BUILDER], {
              replaceUrl: true,
              queryParams: { formId: res.data?._id }
            })
          }
          AppNotification.success("Saved!")
        },
        error: () => {
          AppNotification.success("Error Occured!")
        }
      })
    }

    const sendObj: any = {
      "name": this.formName,
      "survey": this.surveyId,
      "formState": {},
      "formJson": JSON.stringify(this.subscriptionForm.content_dnd),
    }
    if(this.groupId) {
      sendObj.groupId = this.groupId
    }
    return this.formService.add(sendObj).subscribe({
      next: (res: any) => {
        AppNotification.success("Saved!")
        if (res.data?._id) {
          this.formId = res.data?._id
          this.router.navigate([APP_ROUTES.FORM_BUILDER], {
            replaceUrl: true,
            queryParams: { formId: res.data?._id }
          })
        }
      },
      error: () => {
        AppNotification.success("Error Occured!")
      }
    })
  }

  getFormInfo() {
    if (this.formId == null) return;
    this.formService.info(this.formId).subscribe({
      next: (res: any) => {
        this.formName = res.data.name
        this.surveyId = res.data.survey
        this.subscriptionForm.content_dnd = JSON.parse(res.data.formJson)
        this.formBuilderService.formJsonFixer(this.subscriptionForm.content_dnd);
        console.log(this.subscriptionForm.content_dnd)
      },
      error: () => { }
    })
  }

  goBack() {
    return this.router.navigate([APP_ROUTES.SURVEY_DETAILS], {
      queryParams: { surveyId: this.surveyId }
    })
  }

  setViewMode(mode: string) {
    switch (mode) {
      case 'mobile':
        this.formViewClass = 'mobile-mode';
        break;

      case 'tablet':
        this.formViewClass = 'tablet-mode';
        break;

      default:
        this.formViewClass = 'desktop-mode';
        break;
    }
  }
}
